import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send, Loader2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal, MaskedLine } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — a human will get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't send your message. Try emailing us directly.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-2xl border border-line bg-white/70 px-5 py-3.5 text-sm text-ink placeholder:text-mutedwarm/60 outline-none transition-all duration-300 focus:border-terra focus:ring-2 focus:ring-terra/20";

  return (
    <div data-testid="contact-page" className="min-h-screen bg-cream">
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 pb-24 pt-32 sm:px-6 sm:pt-40 lg:px-8">
        <div className="grid gap-16 lg:grid-cols-2">
          <div>
            <Reveal>
              <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-terra">
                Contact
              </p>
            </Reveal>
            <h1 className="mt-6 font-serif text-5xl leading-[1.05] tracking-tight text-ink sm:text-6xl">
              <MaskedLine delay={0.1}>Say</MaskedLine>
              <MaskedLine delay={0.22} className="italic text-terra">hello.</MaskedLine>
            </h1>
            <Reveal delay={0.3}>
              <p className="mt-6 max-w-md text-lg leading-relaxed text-mutedwarm">
                Questions, feedback, or a feature you'd love to see before
                launch? We read everything — and a real human replies.
              </p>
            </Reveal>

            <Reveal delay={0.4}>
              <a
                href="mailto:humans@onedaybuild.co"
                data-testid="contact-email-card"
                className="group mt-10 flex items-center gap-4 rounded-3xl border border-line bg-cardcream p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-terra/40"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-terrasoft transition-colors duration-300 group-hover:bg-terra">
                  <Mail size={20} className="text-terra transition-colors duration-300 group-hover:text-cream" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-[0.2em] text-mutedwarm">
                    Email us directly
                  </span>
                  <span className="mt-1 block font-medium text-ink group-hover:text-terra">
                    humans@onedaybuild.co
                  </span>
                </span>
              </a>
            </Reveal>
          </div>

          <Reveal delay={0.25}>
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="rounded-[2rem] border border-line bg-cardcream p-8 sm:p-10"
            >
              <div className="space-y-5">
                <div>
                  <label htmlFor="contact-name" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-mutedwarm">
                    Name
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    required
                    maxLength={120}
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your name"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-mutedwarm">
                    Email
                  </label>
                  <input
                    id="contact-email"
                    data-testid="contact-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@example.com"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-mutedwarm">
                    Message
                  </label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    required
                    maxLength={4000}
                    rows={6}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Tell us what's on your mind…"
                    className={`${inputCls} resize-none`}
                  />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={sending}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-terra px-7 py-4 text-sm font-semibold text-cream transition-all duration-300 hover:scale-[1.01] hover:bg-terradark disabled:opacity-60"
                >
                  {sending ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      <Send size={16} /> Send message
                    </>
                  )}
                </button>
                <p className="text-center text-xs text-mutedwarm">
                  Goes straight to humans@onedaybuild.co
                </p>
              </div>
            </form>
          </Reveal>
        </div>
      </main>
      <Footer />
    </div>
  );
}
